import { startOfMonth, subMonths } from "date-fns";

import type { MonthlyReportDateRangeDto } from "@/api/types";
import { parseChartDate } from "@/components/Charts/util";

export interface DateRangeOption {
  name: string;
  value: "last6Months" | "lastYear";
}

/**
 * Calculate the start date for a given date range option
 */
export function getDateRangeStart(range: DateRangeOption): Date {
  const now = new Date();
  const monthsToSubtract = range.value === "last6Months" ? 5 : 11;

  return startOfMonth(subMonths(now, monthsToSubtract));
}

/**
 * Filter data based on actual dates instead of array position
 * @param data - Array of data items
 * @param range - Date range option
 * @param dateSelector - Function to extract the date from each item
 */
export function filterDataByDateRange<T extends { range: MonthlyReportDateRangeDto }>(
  data: T[],
  range: DateRangeOption,
): T[] {
  const dateSelector = (item: T) => parseChartDate(item.range);
  const rangeStart = getDateRangeStart(range);

  return data.filter((item) => dateSelector(item) >= rangeStart);
}

/**
 * Split a line into multiple lines in order to not interpolate gaps
 */
export function splitLine<T, TSelector>(items: T[], keySelector?: (item: T) => TSelector): SplitLine<T> {
  const lines: SplitLine<T> = [[]];

  let lastWasDefined = false;
  for (const item of items) {
    if (keySelector ? keySelector(item) : item) {
      lines[lines.length - 1].push(item);
      lastWasDefined = true;
    } else {
      if (lastWasDefined) {
        lines.push(lines[lines.length - 1]?.map(() => undefined) ?? []);
      }

      lines[lines.length - 1].push(undefined);
      lastWasDefined = false;
    }
  }

  if (!lastWasDefined) {
    return lines.slice(0, lines.length - 1);
  }

  return lines;
}

type SplitLine<TItemValue> = (TItemValue | undefined)[][];
